import React, { useState } from 'react'
import { useLang } from '../lib/useLang'

const RELATION_TYPE_KEYS = [
  { value: 'n:1', labelKey: 'relationsModal.manyToOne' },
  { value: '1:n', labelKey: 'relationsModal.oneToMany' },
  { value: 'n:n', labelKey: 'relationsModal.manyToMany' },
  { value: '1:1', labelKey: 'relationsModal.oneToOne' },
  { value: 'auto', labelKey: 'relationsModal.autoDetect' },
]

function RelationCard({ relation, tables, onUpdate, onRemove }) {
  const { t } = useLang()
  const getTableName = tableId => {
    const table = tables.find(t => String(t.id) === String(tableId))
    return table?.name || t('relationsModal.tableFallback', { id: tableId })
  }

  const getTableColumns = tableId => {
    const table = tables.find(t => String(t.id) === String(tableId))
    return Array.isArray(table?.columns) ? table.columns : []
  }

  const leftTableName = getTableName(relation.leftTableId)
  const rightTableName = getTableName(relation.rightTableId)
  const leftColumns = getTableColumns(relation.leftTableId)
  const rightColumns = getTableColumns(relation.rightTableId)

  return (
    <div
      className='group rounded-xl p-5 transition-all duration-200'
      style={{
        background: 'rgba(14, 14, 16, 0.02)',
        border: '1px solid rgba(14, 14, 16, 0.04)',
      }}
    >
      <div className='flex items-center justify-between gap-4'>
        {/* Left Table */}
        <div className='flex-1'>
          <label className='mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-ink'>
            {t('relationsModal.fromTable')}
          </label>
          <div
            className='rounded-lg px-3 py-2.5'
            style={{
              background: 'rgba(255, 90, 31, 0.06)',
              border: '1px solid rgba(255, 90, 31, 0.12)',
            }}
          >
            <div className='truncate text-sm font-medium text-accent'>{leftTableName}</div>
          </div>
          <div className='mt-2'>
            <label className='mb-1 block text-[10px] font-medium uppercase tracking-wider text-ink'>
              {t('relationsModal.joinColumn')}
            </label>
            <div className='relative'>
              <select
                value={relation.leftTableJoiningColumn || ''}
                onChange={e => onUpdate('leftTableJoiningColumn', e.target.value || null)}
                className='mod-field w-full appearance-none rounded-lg px-3 py-2 pr-8 text-xs text-ink'
              >
                <option value='' className='bg-white text-ink'>{t('relationsModal.selectColumn')}</option>
                {leftColumns.map(col => (
                  <option key={col.name} value={col.name} className='bg-white text-ink'>
                    {col.name}
                  </option>
                ))}
              </select>
              <svg
                width='10'
                height='10'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink'
              >
                <path d='m6 9 6 6 6-6' />
              </svg>
            </div>
          </div>
        </div>

        {/* Arrow & Type */}
        <div className='flex flex-col items-center gap-2 px-2'>
          <div className='flex h-8 w-8 items-center justify-center rounded-full bg-ink/[0.03]'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='text-ink'>
              <path d='M5 12h14M12 5l7 7-7 7' />
            </svg>
          </div>
          <div className='relative'>
            <select
              value={relation.relationTypeId}
              onChange={e => onUpdate('relationTypeId', e.target.value)}
              className='mod-field appearance-none rounded-lg px-2 py-1 pr-6 text-[10px] text-ink'
            >
              {RELATION_TYPE_KEYS.map(opt => (
                <option key={opt.value} value={opt.value} className='bg-white text-ink'>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
            <svg
              width='8'
              height='8'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              className='pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-ink'
            >
              <path d='m6 9 6 6 6-6' />
            </svg>
          </div>
        </div>

        {/* Right Table */}
        <div className='flex-1'>
          <label className='mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-ink'>
            {t('relationsModal.toTable')}
          </label>
          <div
            className='rounded-lg px-3 py-2.5'
            style={{
              background: 'rgba(196, 61, 16, 0.06)',
              border: '1px solid rgba(196, 61, 16, 0.12)',
            }}
          >
            <div className='truncate text-sm font-medium text-accent'>{rightTableName}</div>
          </div>
          <div className='mt-2'>
            <label className='mb-1 block text-[10px] font-medium uppercase tracking-wider text-ink'>
              {t('relationsModal.joinColumn')}
            </label>
            <div className='relative'>
              <select
                value={relation.rightTableJoiningColumn || ''}
                onChange={e => onUpdate('rightTableJoiningColumn', e.target.value || null)}
                className='mod-field w-full appearance-none rounded-lg px-3 py-2 pr-8 text-xs text-ink'
              >
                <option value='' className='bg-white text-ink'>{t('relationsModal.selectColumn')}</option>
                {rightColumns.map(col => (
                  <option key={col.name} value={col.name} className='bg-white text-ink'>
                    {col.name}
                  </option>
                ))}
              </select>
              <svg
                width='10'
                height='10'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink'
              >
                <path d='m6 9 6 6 6-6' />
              </svg>
            </div>
          </div>
        </div>

        {/* Remove Button */}
        <button
          onClick={onRemove}
          className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-ink transition-all hover:bg-red-500/10 hover:text-red-600'
        >
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
          </svg>
        </button>
      </div>
    </div>
  )
}

function TableButton({ table, isSelected, onClick }) {
  const { t } = useLang()
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl p-3 text-left transition-all duration-200 ${
        isSelected ? 'ring-1 ring-accent/40' : ''
      }`}
      style={{
        background: isSelected
          ? 'linear-gradient(135deg, rgba(255, 90, 31, 0.12) 0%, rgba(196, 61, 16, 0.06) 100%)'
          : 'rgba(14, 14, 16, 0.02)',
        border: `1px solid ${isSelected ? 'rgba(255, 90, 31, 0.25)' : 'rgba(14, 14, 16, 0.04)'}`,
      }}
    >
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
          isSelected ? 'bg-accent/15' : 'bg-ink/[0.03]'
        }`}
      >
        <svg
          width='14'
          height='14'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.5'
          className={isSelected ? 'text-accent' : 'text-ink'}
        >
          <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
          <polyline points='14,2 14,8 20,8' />
        </svg>
      </div>
      <div className='min-w-0 flex-1'>
        <div className={`truncate text-sm font-medium ${isSelected ? 'text-ink' : 'text-ink'}`}>
          {table.name}
        </div>
        <div className='text-[10px] text-ink'>
          {table.columns?.length || 0} {t('relationsModal.cols')}
        </div>
      </div>
      {isSelected && (
        <div className='h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-accent/15' />
      )}
    </button>
  )
}

export default function RelationsModal({ open, onClose, tables = [], onTablesChange, onRelationsChange, initialRelations }) {
  const [relations, setRelations] = useState(initialRelations || [])
  const [selectedTableId, setSelectedTableId] = useState(null)
  const { t } = useLang()

  if (!open) return null

  const handleTableClick = tableId => {
    if (selectedTableId === null) {
      setSelectedTableId(tableId)
    } else if (selectedTableId === tableId) {
      setSelectedTableId(null)
    } else {
      const relationExists = relations.some(
        rel =>
          (rel.leftTableId === selectedTableId && rel.rightTableId === tableId) ||
          (rel.leftTableId === tableId && rel.rightTableId === selectedTableId)
      )

      if (relationExists) {
        const table1Name = tables.find(t => String(t.id) === String(selectedTableId))?.name || 'Table'
        const table2Name = tables.find(t => String(t.id) === String(tableId))?.name || 'Table'
        alert(t('relationsModal.connectionExists', { table1: table1Name, table2: table2Name }))
        setSelectedTableId(null)
        return
      }

      const newRelation = {
        id: `rel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        leftTableId: selectedTableId,
        rightTableId: tableId,
        relationTypeId: 'auto',
        leftTableJoiningColumn: null,
        rightTableJoiningColumn: null,
      }
      setRelations([...relations, newRelation])
      setSelectedTableId(null)
    }
  }

  const updateRelation = (relationId, key, value) => {
    setRelations(relations.map(rel => (rel.id === relationId ? { ...rel, [key]: value } : rel)))
  }

  const removeRelation = relationId => {
    setRelations(relations.filter(rel => rel.id !== relationId))
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-8'>
      {/* Backdrop */}
      <div
        className='absolute inset-0 bg-black/70 backdrop-blur-sm'
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className='relative flex h-[80vh] w-full max-w-5xl flex-col rounded-2xl'
        style={{
          background: 'linear-gradient(180deg, rgba(20, 20, 24, 0.98) 0%, rgba(12, 12, 14, 0.99) 100%)',
          border: '1px solid rgba(14, 14, 16, 0.05)',
          boxShadow: '0 25px 50px -12px rgba(14, 14, 16, 0.25)',
        }}
      >
        {/* Header */}
        <div
          className='flex items-center justify-between px-8 py-5'
          style={{ borderBottom: '1px solid rgba(14, 14, 16, 0.04)' }}
        >
          <div>
            <h2 className='text-lg font-medium text-ink'>{t('relationsModal.tableRelations')}</h2>
            <p className='mt-0.5 text-sm text-ink'>{t('relationsModal.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className='flex h-8 w-8 items-center justify-center rounded-lg text-ink transition-colors hover:bg-ink/[0.05] hover:text-ink'
          >
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className='flex flex-1 overflow-hidden'>
          {/* Left: Relations List */}
          <div className='flex flex-1 flex-col overflow-hidden' style={{ borderRight: '1px solid rgba(14, 14, 16, 0.04)' }}>
            <div className='px-6 py-4'>
              <h3 className='text-sm font-medium text-ink'>{t('relationsModal.relations')}</h3>
              <p className='mt-0.5 text-xs text-ink'>
                {t('relationsModal.connectionsDefined', { count: relations.length })}
              </p>
            </div>

            <div className='flex-1 space-y-3 overflow-y-auto px-6 pb-6' style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.09) transparent' }}>
              {relations.length === 0 ? (
                <div className='flex h-48 flex-col items-center justify-center text-center'>
                  <svg
                    width='32'
                    height='32'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    className='mb-3 text-ink'
                  >
                    <circle cx='18' cy='5' r='3' />
                    <circle cx='6' cy='12' r='3' />
                    <circle cx='18' cy='19' r='3' />
                    <path d='M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98' />
                  </svg>
                  <p className='text-sm text-ink'>{t('relationsModal.noRelations')}</p>
                  <p className='mt-1 text-xs text-ink'>{t('relationsModal.clickToConnect')}</p>
                </div>
              ) : (
                relations.map(relation => (
                  <RelationCard
                    key={relation.id}
                    relation={relation}
                    tables={tables}
                    onUpdate={(key, value) => updateRelation(relation.id, key, value)}
                    onRemove={() => removeRelation(relation.id)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right: Table Selector */}
          <div className='w-72 flex-shrink-0 overflow-hidden'>
            <div className='px-5 py-4'>
              <h3 className='text-sm font-medium text-ink'>{t('relationsModal.tables')}</h3>
              <p className='mt-0.5 text-xs text-ink'>
                {selectedTableId !== null
                  ? t('relationsModal.clickAnotherTable')
                  : t('relationsModal.clickTwoTables')}
              </p>
            </div>

            <div className='space-y-2 overflow-y-auto px-5 pb-5' style={{ maxHeight: 'calc(100% - 80px)', scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.09) transparent' }}>
              {tables.length === 0 ? (
                <div className='flex h-32 flex-col items-center justify-center text-center'>
                  <p className='text-xs text-ink'>{t('relationsModal.noTables')}</p>
                </div>
              ) : (
                tables.map(table => (
                  <TableButton
                    key={table.id}
                    table={table}
                    isSelected={selectedTableId === table.id}
                    onClick={() => handleTableClick(table.id)}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className='flex items-center justify-end gap-3 px-8 py-4'
          style={{ borderTop: '1px solid rgba(14, 14, 16, 0.04)' }}
        >
          <button
            onClick={onClose}
            className='rounded-xl px-5 py-2.5 text-sm font-medium text-ink transition-all hover:bg-ink/[0.05] hover:text-ink'
          >
            {t('relationsModal.cancel')}
          </button>
          <button
            onClick={() => {
              onRelationsChange?.(relations)
              onClose()
            }}
            className='rounded-xl px-5 py-2.5 text-sm font-medium transition-all'
            style={{
              background: 'linear-gradient(135deg, rgba(255, 90, 31, 0.9) 0%, rgba(196, 61, 16, 0.9) 100%)',
              boxShadow: '0 4px 12px rgba(255, 90, 31, 0.25)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 90, 31, 0.35)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 90, 31, 0.25)'
            }}
          >
            {t('relationsModal.applyRelations')}
          </button>
        </div>
      </div>
    </div>
  )
}
